import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { AppDetailLayout } from '@/shared/app-detail-layout';
import { getTenant, isSuspendEdgeLegal } from '@/modules/tenants/services/api';
import type { Tenant } from '@/api/schemas/tenants';
import type { AppDetailLayoutProps } from '@/shared/app-detail-layout/types';
import { SuspendButton } from './parts/suspend-button';
import { buildSections } from './parts/sections';
import { useTenantSuspend } from './hooks/use-tenant-suspend';

type DetailState =
  | { status: 'loading' }
  | { status: 'error'; error: Error }
  | { status: 'not-found' }
  | { status: 'ready'; tenant: Tenant };

export function DetailPage({ id }: { id: string }): ReactNode {
  const [state, setState] = useState<DetailState>({ status: 'loading' });

  // Hooks must be unconditional — call before any early return.
  const { pending, error: suspendError, suspend } = useTenantSuspend(state.status === 'ready' ? state.tenant : null);

  useEffect(() => {
    let cancelled = false;
    getTenant(id)
      .then((tenant) => {
        if (cancelled) return;
        if (!tenant) setState({ status: 'not-found' });
        else setState({ status: 'ready', tenant });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({ status: 'error', error: error instanceof Error ? error : new Error(String(error)) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (state.status === 'loading' || state.status === 'error' || state.status === 'not-found') {
    const props: AppDetailLayoutProps =
      state.status === 'loading'
        ? { title: 'Loading…', sections: [], isLoading: true }
        : state.status === 'not-found'
          ? { title: 'Tenant', sections: [], notFound: true }
          : { title: 'Tenant', sections: [], error: state.error };
    return <AppDetailLayout {...props} />;
  }

  const { tenant } = state;
  return (
    <AppDetailLayout
      {...buildReadyProps(tenant, pending, suspendError, handleSuspend)}
    />
  );

  async function handleSuspend(): Promise<void> {
    try {
      const updated = await suspend();
      setState((current) =>
        current.status === 'ready'
          ? { ...current, tenant: updated }
          : current,
      );
    } catch {
      // error already set by the hook
    }
  }
}

/**
 * Builds the layout props for the ready state. Pulled out of the component so
 * the orchestrator stays under the 60-line function cap.
 */
function buildReadyProps(
  tenant: Tenant,
  pending: boolean,
  suspendError: Error | null,
  handleSuspend: () => void,
): AppDetailLayoutProps {
  const canSuspend = isSuspendEdgeLegal(tenant.status, 'suspended');
  const suspendAction = canSuspend ? (
    <SuspendButton key="suspend" disabled={pending} onSuspend={handleSuspend} />
  ) : null;

  return {
    headerVariant: 'accent',
    title: tenant.slug,
    subtitle: tenant.legalEntityId ?? 'No legal entity on record',
    status: { label: tenant.status, tone: tenant.status === 'active' ? 'positive' : 'caution' },
    breadcrumbs: [
      { label: 'Platform', href: '/platform' },
      { label: 'Tenants', href: '/platform/tenants' },
      { label: tenant.slug },
    ],
    primaryAction: suspendAction,
    tabsInHeader: true,
    sections: buildSections(tenant),
    tabs: [
      { id: 'overview', label: 'Overview' },
      { id: 'administrators', label: 'Administrators' },
      { id: 'onboarding', label: 'Onboarding' },
      { id: 'verification', label: 'Verification' },
      { id: 'media', label: 'Media' },
      { id: 'capabilities', label: 'Capabilities' },
    ],
    ...(suspendError ? { error: suspendError } : {}),
  };
}